import { Icon, cn } from '@eluve/components';
import type { PlanActivity, PlanEventSpec } from '@eluve/plan-shapes';

import { ActivityList } from './ActivityList';
import { MarkdownProse } from './MarkdownProse';
import type { Accent } from './accents';
import { Eyebrow, IconTile, Panel, PanelInset } from './panel';

// FlareSwimlane — the episodic shape's render primitive: the standing baseline
// lane plus one lane per event in the taxonomy, each pointing at its response.
// Events sit on accent-edged Panels; responses sit on the inset surface for
// internal contrast. Template skeleton only (no patient data).
export function FlareSwimlane({
  baseline,
  baselineActivities,
  events,
  accent,
}: {
  baseline: string;
  baselineActivities?: PlanActivity[];
  events: PlanEventSpec[];
  accent: Accent;
}) {
  return (
    <div className="space-y-3">
      {/* Baseline lane: the care between events, with its home program. */}
      <Panel className={cn('flex items-start gap-3 px-4 py-3.5', accent.edgeL)}>
        <IconTile className={accent.tile}>
          <Icon name="Waves" size="auto" />
        </IconTile>
        <div className="min-w-0 flex-1">
          <Eyebrow className={accent.text}>Baseline</Eyebrow>
          <MarkdownProse
            content={baseline}
            className="mt-1 text-sm text-foreground"
          />
          {baselineActivities && baselineActivities.length > 0 && (
            <ActivityList
              activities={baselineActivities}
              accent={accent}
              className="mt-3"
            />
          )}
        </div>
      </Panel>

      {/* One lane per event: trigger Panel → response inset. */}
      <ul className="space-y-3">
        {events.map((event, i) => (
          <li
            key={i}
            className="flex flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-3"
          >
            <Panel
              className={cn(
                'flex items-start gap-3 px-4 py-3.5 sm:w-2/5',
                accent.edgeL,
              )}
            >
              <IconTile size="sm" className={accent.tile}>
                <Icon name="Zap" size="auto" />
              </IconTile>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">
                  {event.name}
                </p>
                {event.description && (
                  <MarkdownProse
                    content={event.description}
                    className="mt-1 text-xs text-muted-foreground"
                  />
                )}
              </div>
            </Panel>
            <Icon
              name="ArrowRight"
              size="auto"
              className={cn(
                'hidden size-4 shrink-0 self-center sm:block',
                accent.text,
              )}
            />
            <PanelInset className="min-w-0 flex-1 px-4 py-3.5">
              <Eyebrow>Response</Eyebrow>
              <MarkdownProse
                content={event.response}
                className="mt-1 text-sm text-foreground"
              />
              {event.activities && event.activities.length > 0 && (
                <ActivityList
                  activities={event.activities}
                  accent={accent}
                  surface="panel"
                  className="mt-3"
                />
              )}
            </PanelInset>
          </li>
        ))}
      </ul>
    </div>
  );
}
